/**
 * InlineMiniChart — render compacto de chart dentro del chat AI v2
 *
 * El backend (v2/agentGraph.ts) emite `chart` SSE events cuando una tool
 * añade el campo `chart: { type, title, data, unit, dataKey, labelKey, ... }`
 * a su return. El frontend captura ese evento y asocia el chart al
 * mensaje del asistente para renderizar aquí.
 *
 * Pasa el payload por el MISMO pipeline premium que Mission Control
 * (`chartAdvisor` → `resolveChartViz` → renderers premium / `ChartArtifact`),
 * de modo que el chat elige el tipo correcto (área temporal, income_expense,
 * pie categórico, treemap, barras multi-serie…) en vez de degradar todo a
 * línea. Altura compacta (150px) inline; el botón "expandir" abre el mismo
 * chart en un modal a altura cómoda.
 *
 * Compat: acepta el objeto `chart` completo (forma preferida) o las props
 * sueltas legacy `{ type, title, data, unit, dataKey, labelKey }`.
 *
 * Sólo renderiza cuando VITE_AI_V2==='true' (el padre ya aplica el guard).
 */

import { memo, useMemo, useState, Suspense } from 'react';
import { Maximize2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { chartAdvisor } from '../mission-control/viz/chartAdvisor';
import { resolveChartViz, chartDomain } from '../mission-control/viz/vizRegistry';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

// También diferido: `ChartArtifact` es el fallback genérico y trae recharts igual
// que los nueve del registro. Dejarlo estático mantendría viva la cadena desde
// Layout y el corte no serviría de nada.
const ChartArtifact = lazyWithRetry(() => import('../mission-control/viz/ChartArtifact'));

const INLINE_HEIGHT = 150;
const MODAL_HEIGHT = 380;

// Variants premium cuya lectura a 150px es pobre (treemap proporcional, cascada
// con etiquetas): en el inline se degradan a un ChartArtifact compacto y se
// reservan a tamaño completo para el modal expandido.
const HEAVY_VARIANTS = new Set(['expense_categories', 'waterfall', 'composition']);

// Reduce un variant "pesado" a un shape que ChartArtifact pinta legible en poco alto.
function compactFallback(c) {
  if (c?.variant === 'expense_categories' && Array.isArray(c.categorias)) {
    return { type: 'bar', title: c.title, data: c.categorias, dataKey: 'total', labelKey: 'categoria', unit: c.unit || '€' };
  }
  if (c?.variant === 'waterfall' || c?.variant === 'composition') {
    const items = Array.isArray(c.items) ? c.items : (Array.isArray(c.bars) ? c.bars : c.data);
    if (Array.isArray(items)) return { type: 'bar', title: c.title, data: items, dataKey: 'value', labelKey: 'label', unit: c.unit || '€' };
  }
  return c;
}

function InlineMiniChartBase({ chart, type, title, data, unit, dataKey, labelKey }) {
  const [expanded, setExpanded] = useState(false);

  // Sobre el chart CRUDO (respeta el memo/propsEqual): construye el payload
  // —el objeto `chart` si viene, o las props sueltas legacy—, razona el tipo
  // óptimo y resuelve el renderer premium una sola vez.
  const { c, cv } = useMemo(() => {
    const rawChart = chart && typeof chart === 'object'
      ? chart
      : { type: type ?? 'line', title, data, unit: unit ?? '', dataKey: dataKey ?? 'valor', labelKey: labelKey ?? 'mes' };
    const enriched = chartAdvisor(rawChart);
    return { c: enriched, cv: resolveChartViz(enriched) };
  }, [chart, type, title, data, unit, dataKey, labelKey]);

  const renderable = !!cv || (Array.isArray(c?.data) && c.data.length > 0);
  if (!renderable) return null;

  const domain = (cv && cv.domain) || chartDomain(c);
  const heavy = !!cv && HEAVY_VARIANTS.has(c.variant);

  // Los componentes de gráfico se cargan bajo demanda, así que el punto de render
  // va bajo `Suspense`. El respaldo es un hueco de la misma altura: sin él la
  // conversación daría un salto al llegar el chunk.
  const hueco = (altura) => (
    <div style={{ height: altura }} className="animate-pulse rounded bg-muted" aria-hidden="true" />
  );

  // Inline: premium a 150px salvo variants pesados (degradados a ChartArtifact).
  const inlineNode = (
    <Suspense fallback={hueco(INLINE_HEIGHT)}>
      {(cv && !heavy)
        ? <cv.Component {...cv.props} domain={domain} height={INLINE_HEIGHT} />
        : <ChartArtifact {...(heavy ? compactFallback(c) : c)} domain={domain} height={INLINE_HEIGHT} />}
    </Suspense>
  );

  // Modal: premium completo a altura cómoda.
  const modalNode = (
    <Suspense fallback={hueco(MODAL_HEIGHT)}>
      {cv
        ? <cv.Component {...cv.props} domain={domain} height={MODAL_HEIGHT} />
        : <ChartArtifact {...c} domain={domain} height={MODAL_HEIGHT} />}
    </Suspense>
  );

  const heading = title || c?.title;

  return (
    <div className="mt-2 p-2 bg-muted rounded-lg border border-border">
      <div className="flex items-center justify-end mb-1">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Ampliar gráfico"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Maximize2 className="w-3 h-3" />
        </button>
      </div>

      {inlineNode}

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm">{heading || 'Gráfico'}</DialogTitle>
          </DialogHeader>
          {modalNode}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Sprint 6 T5 — memoized because the parent re-renders on every SSE token of
 * the surrounding assistant message. Without memo, Recharts re-layouts its
 * SVG tree 50-200× per streamed response, visibly jank-ing the chat. El
 * comparador se apoya en la igualdad por referencia del objeto `chart` (forma
 * preferida) y, para los callers legacy, en las props sueltas.
 */
function propsEqual(prev, next) {
  return (
    prev.chart === next.chart &&
    prev.title === next.title &&
    prev.type === next.type &&
    prev.unit === next.unit &&
    prev.dataKey === next.dataKey &&
    prev.labelKey === next.labelKey &&
    prev.data === next.data
  );
}

const InlineMiniChart = memo(InlineMiniChartBase, propsEqual);
export default InlineMiniChart;
